import React from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import * as Polished from 'polished';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CHANGE_PAYMENT_METHOD, GET_CURRENT_PAYMENT_INFO } from '../graphql/queries';
import Button from './Button';
import colors from '../styles/colors';
import Icon from './Icon';
import { IconEnum as Icons } from './Icons';
import { getErrorMessage } from '../util/ErrorUtil';

const Container = styled.div`
  position: relative;
  background-color: ${colors.white};
  overflow: hidden;

  .Element {
    background: ${colors.white};
    border-radius: 10px;
    transition: box-shadow 0.1s ease, border-color 0.1s ease;
    box-shadow: none;
    padding:  1.6rem;
    padding-left: 4rem;
  }
`;

const Form = styled.form`
  /* display: flex; */
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
  flex: 1;
`;

const Spacer = styled.div`
  flex: 0.075;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 15px;
  left: 15px;
`;

const ElementContainer = styled.div`
  position: relative;
  flex: 1;

  .Element {
    border: 1px solid
      ${(props) => {
    if (props.focused) return colors.grey4;
    return colors.grey5;
  }};

    &:hover {
      border: 1px solid
        ${(props) => {
    if (props.focused) return colors.grey4;
    return Polished.darken(0.05, colors.grey5);
  }};
    }
  }
`;

const ElementsEnum = Object.freeze({
  CardNumber: 'CardNumber',
  CVC: 'CVC',
  ExpDate: 'ExpDate',
});

export default function ChangePayment({ close, onCompleted, setError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [focused, setFocus] = React.useState(null);
  const [changePaymentMethod, { loading }] = useMutation(CHANGE_PAYMENT_METHOD, {
    refetchQueries: [{ query: GET_CURRENT_PAYMENT_INFO }],
    onError(error) {
      console.log(error);
      setError(getErrorMessage(error));
    },
    onCompleted() {
      if (onCompleted) {
        onCompleted();
      } else {
        close();
      }
    },
  });

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    const { error, token } = await stripe.createToken(cardElement);

    if (error) {
      console.log('[error]', error);
      setError(error.message);
    } else {
      changePaymentMethod({
        variables: {
          cardToken: token.id,
        },
      });
    }
  };

  const style = {
    base: {
      fontSize: '14px',
      fontWeight: '500',
      // height: "40px",
      color: colors.grey1,
      fontSmoothing: 'antialiased',
      fontFamily: 'Poppins',
      '::placeholder': {
        color: colors.grey4,
      },
      ':focus': {},
    },
    invalid: {
      color: colors.red,
    },
  };

  const isFocused = (field) => field === focused;

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <Row>
          <ElementContainer focused={isFocused(ElementsEnum.CardNumber)}>
            <IconContainer>
              <Icon
                icon={Icons.CreditCardFront}
                size={18}
                active
                activeColor={colors.grey2}
              />
            </IconContainer>
            <CardNumberElement
              className="Element"
              options={{ style, placeholder: 'Card Number' }}
              onFocus={() => setFocus(ElementsEnum.CardNumber)}
              onBlur={() => setFocus(null)}
            />
          </ElementContainer>
        </Row>
        <Row>
          <ElementContainer focused={isFocused(ElementsEnum.CVC)}>
            <IconContainer>
              <Icon
                icon={Icons.CreditCardBack}
                size={18}
                active
                activeColor={colors.grey2}
              />
            </IconContainer>
            <CardCvcElement
              className="Element"
              options={{ style, placeholder: 'CVC' }}
              onFocus={() => setFocus(ElementsEnum.CVC)}
              onBlur={() => setFocus(null)}
            />
          </ElementContainer>
          <Spacer />
          <ElementContainer focused={isFocused(ElementsEnum.ExpDate)}>
            <IconContainer>
              <Icon
                icon={Icons.Calendar}
                size={18}
                active
                activeColor={colors.grey2}
              />
            </IconContainer>
            <CardExpiryElement
              className="Element"
              options={{ style, placeholder: 'Exp. Date' }}
              onFocus={() => setFocus(ElementsEnum.ExpDate)}
              onBlur={() => setFocus(null)}
            />
          </ElementContainer>
        </Row>
        <Button
          margin="20px 0px 0px 0px"
          text="CONFIRM"
          width="100%"
          type="submit"
          loading={loading}
        />
      </Form>
    </Container>
  );
}
